import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  {
    step: '01',
    title: 'Conte seu sonho',
    description: 'Conversamos para entender seus desejos, expectativas, orçamento e o estilo de viagem que você busca. Cada detalhe importa.',
    icon: 'ri-chat-heart-line',
  },
  {
    step: '02',
    title: 'Planejamos cada detalhe',
    description: 'Nossa equipe de especialistas cria um roteiro personalizado com as melhores opções de voos, hotéis, passeios e experiências exclusivas.',
    icon: 'ri-compass-3-line',
  },
  {
    step: '03',
    title: 'Você embarca sem preocupações',
    description: 'Com tudo organizado, você viaja com total tranquilidade. Nosso suporte 24h garante que cada momento seja perfeito.',
    icon: 'ri-flight-takeoff-line',
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const items = sectionRef.current?.querySelectorAll('.timeline-item');
      if (items) {
        gsap.fromTo(
          items,
          { x: -30, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 0.7,
            stagger: 0.2,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 75%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full py-24 md:py-32 px-6 md:px-10 lg:px-16 bg-background-100">
      <div className="max-w-[1440px] mx-auto">
        <div className="text-center mb-16 md:mb-20">
          <span className="inline-block text-xs tracking-[0.2em] uppercase text-accent-600 font-label mb-4">
            Como funciona
          </span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-foreground-900 leading-tight">
            Sua jornada <span className="italic">em 3 passos</span>
          </h2>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-background-300/80 hidden md:block"></div>

            <div className="space-y-16 md:space-y-20">
              {steps.map((item, index) => (
                <div key={index} className="timeline-item flex flex-col md:flex-row gap-6 md:gap-12 items-start">
                  {/* Step number */}
                  <div className="relative z-10 flex-shrink-0 w-16 h-16 rounded-full bg-background-50 border-2 border-accent-200 flex items-center justify-center">
                    <span className="font-label text-accent-600 text-lg font-semibold">
                      {item.step}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pt-2">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-accent-50 rounded-lg">
                        <i className={`${item.icon} text-accent-600 text-xl`}></i>
                      </div>
                      <h3 className="font-heading text-2xl md:text-3xl font-medium text-foreground-900">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-foreground-600 leading-relaxed text-sm md:text-base ml-0 md:ml-[52px]">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}